import axios from 'axios';
import config from '../config';
import { createBrowserHistory } from 'history';

import { store } from '../store';
import { refreshAuthToken } from '../services/auth';

const history = createBrowserHistory();

let isRefreshing = false;
let requestQueue = [];

const processQueue = (error, token = null) => {
  requestQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  requestQueue = [];
};

// Centralized logout function
const performLogout = async (error) => {
  console.error('Performing logout due to auth failure:', error);
  processQueue(error, null);

  store.dispatch({ type: 'SET_INITIAL_STATE' });
  alert('Session expired. Please login again.');
  history.push('/login');

  setTimeout(() => {
    store.dispatch({
      type: 'SHOW_ERROR',
      payload: {
        errorTitle: 'Session Expired',
        errorDesc: 'Your session has expired. Please login again.',
      },
    });
  }, 500);
  isRefreshing = false; // Reset refreshing state
};

// Create custom error for refresh token failure
const createRefreshTokenError = () => {
  const error = new Error();
  error.name = 'RefreshTokenExpiredError';
  error.isRefreshTokenError = true;
  error.userFriendly = true;
  return error;
};

// Configure default axios instance
axios.defaults.baseURL = config.apiBaseUrl;
axios.defaults.withCredentials = true;

// Create a separate axios instance for refresh token calls (no interceptors)
export const refreshAxiosInstance = axios.create({
  baseURL: config.apiBaseUrl,
  withCredentials: true,
});

// Attach authToken before requests (applies to all axios requests)
axios.interceptors.request.use((config) => {
  const token = store.getState().user.authToken;
  if (token) {
    config.headers.Authorization = token;
  }
  if (store.getState().app?.config?.entityId) {
    // sent dope entity id in headers
    // This will not exist in pre-login flow
    config.headers['entityId'] = store.getState().app.config.entityId;
  }
  return config;
});

// Handle 401 responses (applies to all axios requests)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Early return if refresh token feature is disabled - just pass through the error
    if (!config.features?.enableRefreshToken) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    if (error.response?.status === 401) {
      // Check if response contains sessionInvalidated: true
      const responseData = error.response?.data;
      if (responseData?.sessionInvalidated === true) {
        console.error('Session invalidated by server. Logging out user.');
        const customError = createRefreshTokenError();
        performLogout(customError);
        return Promise.reject(customError);
      }

      // Check if this 401 is from the refresh token API itself (check URL)
      const isRefreshRequest = originalRequest.url?.includes(
        '/new-auth/user/refresh'
      );

      if (isRefreshRequest) {
        // If refresh token API fails with 401, it means refresh token is expired
        // User needs to login again
        console.error('Refresh token expired. Logging out user.');
        const customError = createRefreshTokenError();

        // performLogout will handle calling authLogout() to clear cookies
        performLogout(customError);
        return Promise.reject(customError);
      }

      // For other APIs with 401, check if we haven't already retried
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          // Queue this request to be retried after refresh completes
          return new Promise((resolve, reject) => {
            requestQueue.push({
              resolve: (token) => {
                originalRequest.headers.Authorization = token;
                resolve(axios(originalRequest));
              },
              reject: (error) => {
                reject(error);
              },
            });
          });
        }

        isRefreshing = true;

        try {
          const refreshResult = await refreshAuthToken();
          const newToken = refreshResult.headers.authorization;

          // Update Redux store and axios defaults
          store.dispatch({ type: 'AUTH_TOKEN', payload: newToken });
          axios.defaults.headers.common['Authorization'] = newToken;

          // Update the original request with the new token before retrying
          originalRequest.headers.Authorization = newToken;

          // Process all queued requests with the new token
          processQueue(null, newToken);

          // Retry the original request
          return axios(originalRequest);
        } catch (err) {
          console.error('Refresh token failed:', err);
          const customError = createRefreshTokenError();
          performLogout(customError);
          return Promise.reject(customError);
        } finally {
          isRefreshing = false;
        }
      }
    }

    return Promise.reject(error);
  }
);

// Export the configured axios instance
export const axiosInstance = axios;
